import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { CollectionGameHelper } from '@sorare/core/src/components/collections/CollectionGameHelper';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';

import type { CollectionUpsellTitle_cardCollection } from './__generated__/index.graphql';

const Title = styled(Horizontal)`
  padding: 0 var(--double-unit);
`;

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--triple-unit);
`;

const DrawerWrapper = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
  background-color: var(--c-nl-900);
  border-top-left-radius: var(--double-unit);
  border-top-right-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
`;

const StyledCloseButton = styled(CloseButton)`
  align-self: flex-end;
`;

type Props = {
  cardCollection: CollectionUpsellTitle_cardCollection;
};

export const CollectionUpsellTitle = ({ cardCollection }: Props) => {
  const [open, setOpen] = useState(false);
  const { up: upTablet } = useScreenSize('tablet');

  return (
    <Title>
      <HeadlineS>
        <FormattedMessage
          id="cart.collectionUpsell.title"
          defaultMessage="Boost your collections"
        />
      </HeadlineS>
      <IconButton
        onClick={() => setOpen(true)}
        icon={faInfoCircle}
        color="transparent"
        size="compact"
      />
      {upTablet ? (
        <Dialog
          maxWidth="xs"
          fullWidth
          open={open}
          onClose={() => setOpen(false)}
        >
          <Wrapper>
            <CollectionGameHelper cardCollection={cardCollection} />
          </Wrapper>
        </Dialog>
      ) : (
        <Drawer
          open={open}
          side="bottom"
          onClose={() => setOpen(false)}
          onBackdropClick={() => setOpen(false)}
          blur
        >
          <DrawerWrapper>
            <StyledCloseButton size="medium" onClose={() => setOpen(false)} />
            <CollectionGameHelper cardCollection={cardCollection} />
          </DrawerWrapper>
        </Drawer>
      )}
    </Title>
  );
};

CollectionUpsellTitle.fragments = {
  cardCollection: gql`
    fragment CollectionUpsellTitle_cardCollection on CardCollection {
      slug
      ...CollectionGameHelper_cardCollection
    }
    ${CollectionGameHelper.fragments.cardCollection}
  ` as TypedDocumentNode<CollectionUpsellTitle_cardCollection>,
};
